// From @fuze/tokens, not @fuze/design-system: the design-system entry has no `sideEffects: false`, so importing
// anything from it bundles every Nexus component into the initial chunk.
import { type Brand, type ColorMode, type Density, getTheme } from '@fuze/tokens';
import { type ReactNode, useEffect, useMemo } from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';

// exception: docs/conventions/styling.md, Tokens
// The system fonts are fallbacks for while Inter loads or if it fails. @fuze/tokens publishes only the family name,
// and Nexus's own Storybook uses this same stack. Drop them when @fuze/tokens publishes a full stack.
const GlobalStyle = createGlobalStyle`
  *, *::before, *::after {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    font-family: ${({ theme }) => theme.typography.body.regular.fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: ${({ theme }) => theme.colors.content.neutral.default};
    background-color: ${({ theme }) => theme.colors.surface.neutral.base};
  }
`;

export type PortalThemeProviderProps = {
  /** The program's Nexus brand, e.g. `ucbOnward`. Comes from the app's program content. */
  brand: Brand;
  mode?: ColorMode | undefined;
  density?: Density | undefined;
  children: ReactNode;
};

/**
 * The `data-theme` value the Fuze Panda preset keys each brand's tokens on (`[data-theme=ucb-onward] &`). The default
 * brand has none: its tokens are the preset's base values.
 */
const PANDA_THEME: Record<Brand, string | undefined> = { fuzeRx: undefined, ucbOnward: 'ucb-onward' };

/**
 * The Nexus theme for a portal: styled-components ThemeProvider with the program's brand, plus the page-level
 * base styles every Nexus component assumes (box-sizing, Inter, body colors). Fonts and the Material Symbols
 * icon font are imported once by the app entry (and Storybook), not here.
 */
export const PortalThemeProvider = ({
  brand,
  mode = 'light',
  density = 'comfortable',
  children,
}: PortalThemeProviderProps) => {
  // Deprecated in favor of Nexus's Panda preset, but the components Nexus hasn't migrated yet (Input, Nav, Tabs,
  // Drawer, ...) still read this theme object through styled-components. Remove it when the last one migrates.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const theme = useMemo(() => getTheme(mode, density, brand), [mode, density, brand]);
  // The portal's own Panda styles read their brand from the document, so portals (dialogs) inherit it too.
  useEffect(() => {
    const value = PANDA_THEME[brand];
    if (value === undefined) {
      delete document.documentElement.dataset.theme;
    } else {
      document.documentElement.dataset.theme = value;
    }
  }, [brand]);
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      {children}
    </ThemeProvider>
  );
};
